const EARTH_RADIUS_M = 6371000;

export const SHOPPING_AREAS = [
  { id: 'amsterdam_zuid_as',  name: 'Amsterdam Zuid AS',                lat: 52.3367, lng: 4.8688, radius: 10000 },
  { id: 'amsterdamse_poort',  name: 'Winkelcentrum Amsterdamse Poort',  lat: 52.3139, lng: 4.9525, radius: 10000 },
  { id: 'alexandrium',        name: 'Alexandrium Rotterdam',            lat: 51.9490, lng: 4.5582, radius: 10000 },
  { id: 'citymall_almere',    name: 'Citymall Almere',                  lat: 52.3703, lng: 5.2141, radius: 10000 },
  { id: 'gelderlandplein',    name: 'Winkelcentrum Gelderlandplein',    lat: 52.3389, lng: 4.8774, radius: 10000 },
  { id: 'stadshart',          name: 'Stadshart Lelystad',               lat: 52.5088, lng: 5.4721, radius: 10000 },
  { id: 'hoge_schie',         name: 'Shopping Center Hoge Schie',       lat: 51.9225, lng: 4.4792, radius: 10000 },
];

// Address → coordinates via OpenStreetMap Nominatim. Returns the raw result
// list (each entry has lat, lon and display_name) so callers can offer the
// matches for the user to pick from. Shared by the admin shop forms and the
// retailer's own profile page.
export async function geocodeAddress(query, { limit = 5, restrictToNL = true } = {}) {
  if (!query || !query.trim()) return [];
  const q = restrictToNL ? `${query.trim()}, Netherlands` : query.trim();
  const url =
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}` +
    `&format=json&limit=${limit}` +
    (restrictToNL ? '&countrycodes=nl' : '');
  const res = await fetch(url, {
    headers: {
      'Accept-Language': 'nl',
      // Nominatim's usage policy asks for an identifying User-Agent. Browsers
      // treat User-Agent as a forbidden header and drop it, so it only takes
      // effect if this ever runs server-side; the browser's own UA is sent.
      'User-Agent': 'WalkinDeals/1.0',
    },
  });
  if (!res.ok) throw new Error(`Geocoding failed (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// Single best match, for forms that just need one set of coordinates.
export async function geocodeOne(query, options = {}) {
  const results = await geocodeAddress(query, { ...options, limit: 1 });
  if (results.length === 0) return null;
  const r = results[0];
  const lat = parseFloat(r.lat);
  const lng = parseFloat(r.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng, displayName: r.display_name };
}

// Haversine formula — accurate to within ~5m over city-scale distances
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

// Dutch-friendly distance label: "85 m", "950 m", "1,2 km", "2,4 km".
// Returns '' for anything that is not a usable distance, so callers hide the
// label instead of rendering one.
//
// A rounded value of zero counts as unusable. In this data a 0 is practically
// always an artefact — a distance measured from the same point the deal sits
// on — rather than a walker standing on the doorstep, and "0 m" reads as a
// broken app either way. This is the single choke point every distance on the
// home page passes through, so guarding here means no code path can put a
// "0 m" back on screen.
export function formatDistanceNL(meters) {
  if (meters == null || meters === '') return '';
  const n = Number(meters);
  if (!Number.isFinite(n) || n < 0) return '';
  const rounded = Math.round(n);
  if (rounded <= 0) return '';
  if (rounded < 1000) return `${rounded} m`;
  return `${(n / 1000).toFixed(1).replace('.', ',')} km`;
}

// Returns ALL areas within radius, sorted by distance (closest first)
export function findNearbyAreas(userLat, userLng) {
  return SHOPPING_AREAS
    .map((area) => {
      const dist = calculateDistance(userLat, userLng, area.lat, area.lng);
      return { ...area, distanceToCenter: dist };
    })
    .filter((area) => area.distanceToCenter <= area.radius)
    .sort((a, b) => a.distanceToCenter - b.distanceToCenter);
}

// Returns the single closest area (or null) — kept for backward compat
export function findNearbyArea(userLat, userLng) {
  const areas = findNearbyAreas(userLat, userLng);
  return areas.length > 0 ? areas[0] : null;
}

function _getPos(options) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const err = new Error('Geolocation not supported');
      err.code = 0;
      reject(err);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      reject,
      options,
    );
  });
}

export async function requestLocationPermission() {
  if (!navigator.geolocation) return false;
  if (navigator.permissions) {
    try {
      const perm = await navigator.permissions.query({ name: 'geolocation' });
      if (perm.state === 'denied') return false;
    } catch { /* not supported in all browsers */ }
  }
  try {
    await _getPos({ enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 });
    return true;
  } catch {
    return false;
  }
}

export function getCurrentPosition() {
  return _getPos({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
}

let _intervalId = null;
let _lastPrimaryAreaId;
let _wasInArea = false;

// Callback receives { areas: Area[], position }
export function watchLocation(callback) {
  _lastPrimaryAreaId = undefined;
  _wasInArea = false;

  const check = async () => {
    try {
      const pos = await _getPos({ enableHighAccuracy: false, timeout: 10000, maximumAge: 25000 });
      const areas = findNearbyAreas(pos.lat, pos.lng);
      const primaryAreaId = areas.length > 0 ? areas[0].id : null;

      if (primaryAreaId !== null) _wasInArea = true;

      // Always fire on first check; thereafter only when primary area changes
      if (_lastPrimaryAreaId === undefined || primaryAreaId !== _lastPrimaryAreaId) {
        _lastPrimaryAreaId = primaryAreaId;
        callback({ areas, position: pos });
      }

      // Stop when user walks out of all areas after having been inside one
      if (_wasInArea && primaryAreaId === null) stopWatching();
    } catch { /* network / timeout — retry next interval */ }
  };

  check();
  _intervalId = setInterval(check, 30000);
}

export function stopWatching() {
  if (_intervalId !== null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
}
